import {
    Box,
    Checkbox,
    Divider,
    Flex,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { TbSearch } from 'react-icons/tb';
import { SettingsInlineEdit } from './primitives';
import { InlineCellDisplay } from './UsersInlineCells';
import {
    formatMakeList,
    isSameMakeList,
    toMakeIds,
    toMakeList,
    type MakeList,
    type MakeOption,
} from './makesListValue';

export interface MakesPickerProps {
    value: Array<string>;
    options: MakeOption[];
    onChange: (next: Array<string>) => void;
    maxH?: string;
}

/**
 * All Makes toggle over a searchable checkbox list, the makes counterpart of
 * LocationsPicker. Unlike locations there is no 'all' sentinel to store — a
 * record holds the makes it names — so All Makes ticks every option instead.
 */
export const MakesPicker = ({ value, options, onChange, maxH = '220px' }: MakesPickerProps) => {
    const [search, setSearch] = useState('');

    const selectable = useMemo(
        () =>
            options
                .filter((option) => option.name && option.name.trim() !== '')
                .sort((a, b) => a.name.localeCompare(b.name)),
        [options],
    );

    const visibleOptions = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return selectable;
        return selectable.filter((option) => option.name.toLowerCase().includes(query));
    }, [selectable, search]);

    const selectedIds = new Set(value);
    const isAll = selectable.length > 0 && selectable.every((option) => selectedIds.has(option._id));

    const toggle = (id: string, checked: boolean) => {
        onChange(
            checked
                ? Array.from(new Set([...value, id]))
                : value.filter((current) => current !== id),
        );
    };

    return (
        <VStack align="stretch" spacing={2}>
            <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                    <Icon as={TbSearch as React.ElementType} color="gray.400" />
                </InputLeftElement>
                <Input
                    value={search}
                    placeholder="Search makes"
                    onChange={(e) => setSearch(e.target.value)}
                    borderRadius="md"
                />
            </InputGroup>
            {/* Checkboxes rather than the tinted option rows the single-select
                cells use: All Makes marks every make selected, and a whole panel
                of filled rows is unreadable at this length. */}
            <Checkbox
                size="sm"
                colorScheme="partbay-blue"
                isChecked={isAll}
                isIndeterminate={!isAll && selectedIds.size > 0}
                onChange={(e) =>
                    onChange(e.target.checked ? selectable.map((option) => option._id) : [])
                }
            >
                <Text fontSize="sm" fontWeight="medium" color="gray.800">
                    All Makes
                </Text>
            </Checkbox>
            <Divider />
            <VStack align="stretch" spacing={1} maxH={maxH} overflowY="auto">
                {visibleOptions.length === 0 ? (
                    <Text fontSize="sm" color="gray.500" px={1} py={1}>
                        No matches
                    </Text>
                ) : (
                    visibleOptions.map((option) => (
                        <Checkbox
                            key={option._id}
                            size="sm"
                            colorScheme="partbay-blue"
                            isChecked={selectedIds.has(option._id)}
                            onChange={(e) => toggle(option._id, e.target.checked)}
                        >
                            <Text fontSize="sm" color="gray.700" noOfLines={1}>
                                {option.name}
                            </Text>
                        </Checkbox>
                    ))
                )}
            </VStack>
        </VStack>
    );
};

export interface InlineMakesCellProps {
    value: MakeList;
    options: MakeOption[];
    onSave: (next: MakeList) => void;
    isDisabled?: boolean;
    ariaLabel: string;
}

/**
 * Makes cell for records scoped to a set of makes. Saves on close, since
 * picking several makes takes more than one click, and an empty selection is
 * treated as no change — a rule with no makes would never fire.
 */
export const InlineMakesCell = ({
    value,
    options,
    onSave,
    isDisabled = false,
    ariaLabel,
}: InlineMakesCellProps) => {
    const [draft, setDraft] = useState<Array<string>>(() => toMakeIds(value));

    return (
        <SettingsInlineEdit
            display={
                <InlineCellDisplay
                    label={formatMakeList(value, options.length)}
                    isMuted={value.length === 0}
                    hasChevron={!isDisabled}
                />
            }
            ariaLabel={ariaLabel}
            isDisabled={isDisabled}
            minW="260px"
            onOpen={() => setDraft(toMakeIds(value))}
            onClose={() => {
                if (draft.length === 0) return;
                const next = toMakeList(draft, options);
                if (!isSameMakeList(next, value)) onSave(next);
            }}
        >
            <Box>
                <MakesPicker value={draft} options={options} onChange={setDraft} />
                {draft.length === 0 && (
                    <Flex pt={2}>
                        <Text fontSize="xs" color="red.500">
                            Pick at least one make.
                        </Text>
                    </Flex>
                )}
            </Box>
        </SettingsInlineEdit>
    );
};
